/**
 * CrewBulkBar
 *
 * The action bar that appears once crew are selected on the list.
 *
 * What is here, and what is deliberately not: archiving and restoring are
 * offered in bulk because they are reversible and take nothing away — a
 * graduating class is exactly the case this exists for. Messaging and CSV
 * export are read-only. Pay rates are offered in bulk (when the org tracks
 * payment) because a season's rate change is the same number typed into forty
 * profiles otherwise. Deactivation is absent on purpose: it revokes access and
 * cancels assignments, so it stays on the individual profile where the context
 * is.
 */

interface CrewBulkBarProps {
  selectedCount: number;
  /** True when the list is showing archived crew, which flips the verb. */
  showingArchived: boolean;
  busy?: boolean;
  onArchive: () => void;
  onRestore: () => void;
  onMessage: () => void;
  onExport: () => void;
  /** Absent when the org does not track payment — the button is not shown. */
  onUpdateRates?: () => void;
  onClear: () => void;
}

export default function CrewBulkBar({
  selectedCount,
  showingArchived,
  busy = false,
  onArchive,
  onRestore,
  onMessage,
  onExport,
  onUpdateRates,
  onClear,
}: CrewBulkBarProps) {
  if (selectedCount === 0) return null;

  const action = 'px-3 py-2 text-sm font-medium border border-hairline bg-white text-ink hover:bg-rack hover:text-white disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-ink';

  return (
    <div
      className="mb-4 flex flex-wrap items-center gap-3 border border-hairline bg-paper px-4 py-3"
      role="region"
      aria-label="Bulk actions"
    >
      <span className="text-sm font-medium text-ink">
        {selectedCount} selected
      </span>

      <div className="flex flex-wrap gap-2">
        {/* Both directions are always available: a mixed selection is normal
            when the filter is "All", and guessing which one the admin meant
            would be worse than offering both. The server skips whoever is
            already in the target state. */}
        {showingArchived ? (
          <button type="button" onClick={onRestore} disabled={busy} className={action}>
            Restore to roster
          </button>
        ) : (
          <button type="button" onClick={onArchive} disabled={busy} className={action}>
            Archive
          </button>
        )}
        <button type="button" onClick={onMessage} disabled={busy} className={action}>
          Send message
        </button>
        <button type="button" onClick={onExport} disabled={busy} className={action}>
          Export CSV
        </button>
        {onUpdateRates && (
          <button type="button" onClick={onUpdateRates} disabled={busy} className={action}>
            Update pay rates
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={onClear}
        className="ml-auto text-sm text-ink-secondary hover:text-ink underline"
      >
        Clear selection
      </button>
    </div>
  );
}
